export interface ReportPaginationInput {
  sectionHeights: number[];
  headerIndex: number;
  availableHeight: number;
  continuationAvailableHeight?: number;
  renderedOverflowReserve?: number;
  forcedPageBreakBeforeIndexes?: ReadonlySet<number>;
}

interface ReportNarrativeFragmentationInput extends ReportPaginationInput {
  narrativeKeys: Array<string | undefined>;
  narrativeCanFragment: boolean[];
  narrativeFragmentIndexes: Array<number | undefined>;
}

interface PaginationCursor {
  pageIndex: number;
  usedHeight: number;
  hasSections: boolean;
}

interface SectionPlacement {
  height: number;
  pageCapacity: number;
  overflowsCurrentPage: boolean;
}

interface SectionPlacementResult {
  startedNewPage: boolean;
  readableScale?: number;
  unreadable: boolean;
}

interface PageHeightSource {
  clientHeight: number;
  scrollHeight: number;
}

export interface ReportPaginationLayout {
  pages: number[][];
  sectionScales: Record<number, number>;
  unreadableSectionIndexes?: number[];
}

export const MIN_REPORT_SECTION_SCALE = 0.8;
export const REPORT_MEANINGFUL_REMAINING_SPACE_RATIO = 0.25;

export function findNextReportNarrativeKeyToFragment(
  input: ReportNarrativeFragmentationInput
): string | undefined {
  const cursor: PaginationCursor = {
    pageIndex: 0,
    usedHeight: 0,
    hasSections: false,
  };

  for (
    let sectionIndex = 0;
    sectionIndex < input.sectionHeights.length;
    sectionIndex += 1
  ) {
    if (sectionIndex !== input.headerIndex) {
      applyForcedPageBreak(input, cursor, sectionIndex);
      const placement = describeSectionPlacement(input, cursor, sectionIndex);
      if (shouldFragmentNarrative(input, cursor, placement, sectionIndex)) {
        return input.narrativeKeys[sectionIndex];
      }
      placeSection(input, cursor, placement);
    }
  }

  return undefined;
}

function applyForcedPageBreak(
  input: ReportPaginationInput,
  cursor: PaginationCursor,
  sectionIndex: number
): boolean {
  if (
    !cursor.hasSections ||
    !input.forcedPageBreakBeforeIndexes?.has(sectionIndex)
  ) {
    return false;
  }
  cursor.pageIndex += 1;
  cursor.usedHeight = 0;
  cursor.hasSections = false;
  return true;
}

function describeSectionPlacement(
  input: ReportPaginationInput,
  cursor: PaginationCursor,
  sectionIndex: number
): SectionPlacement {
  const height = input.sectionHeights[sectionIndex] ?? 0;
  const pageCapacity = getPageCapacity(input, cursor.pageIndex);
  return {
    height,
    pageCapacity,
    overflowsCurrentPage: cursor.usedHeight + height > pageCapacity,
  };
}

function shouldFragmentNarrative(
  input: ReportNarrativeFragmentationInput,
  cursor: PaginationCursor,
  placement: SectionPlacement,
  sectionIndex: number
): boolean {
  const narrativeKey = input.narrativeKeys[sectionIndex];
  const isWholeNarrative =
    narrativeKey !== undefined &&
    input.narrativeFragmentIndexes[sectionIndex] === undefined;
  if (!isWholeNarrative || input.narrativeCanFragment[sectionIndex] !== true) {
    return false;
  }
  const remainingHeight = Math.max(
    0,
    placement.pageCapacity - cursor.usedHeight
  );
  const hasMeaningfulRemainingSpace =
    cursor.hasSections &&
    remainingHeight >
      placement.pageCapacity * REPORT_MEANINGFUL_REMAINING_SPACE_RATIO;
  const freshPageCapacity =
    cursor.hasSections && placement.overflowsCurrentPage
      ? getPageCapacity(input, cursor.pageIndex + 1)
      : placement.pageCapacity;
  const cannotFitFreshPageReadably =
    placement.height > freshPageCapacity &&
    freshPageCapacity / placement.height < MIN_REPORT_SECTION_SCALE;
  return (
    (placement.overflowsCurrentPage && hasMeaningfulRemainingSpace) ||
    cannotFitFreshPageReadably
  );
}

function placeSection(
  input: ReportPaginationInput,
  cursor: PaginationCursor,
  placement: SectionPlacement
): SectionPlacementResult {
  let startedNewPage = false;
  if (cursor.hasSections && placement.overflowsCurrentPage) {
    cursor.pageIndex += 1;
    cursor.usedHeight = 0;
    startedNewPage = true;
  }
  const targetCapacity = getPageCapacity(input, cursor.pageIndex);
  const readableScale = calculateReadableSectionScale(
    placement.height,
    targetCapacity
  );
  cursor.usedHeight += placement.height * (readableScale ?? 1);
  cursor.hasSections = true;
  return {
    startedNewPage,
    readableScale,
    unreadable:
      readableScale === undefined && placement.height > targetCapacity,
  };
}

export function createReportPaginationLayout(
  input: ReportPaginationInput
): ReportPaginationLayout {
  const layout = groupSectionsIntoPages(input);
  compactPageBoundaries(input, layout);
  return layout;
}

function groupSectionsIntoPages(
  input: ReportPaginationInput
): ReportPaginationLayout {
  const { sectionHeights, headerIndex } = input;
  const pages: number[][] = [[]];
  const sectionScales: Record<number, number> = {};
  const unreadableSectionIndexes: number[] = [];
  const cursor: PaginationCursor = {
    pageIndex: 0,
    usedHeight: 0,
    hasSections: false,
  };
  sectionHeights.forEach((_height, sectionIndex) => {
    if (sectionIndex === headerIndex) return;
    const forcedNewPage = applyForcedPageBreak(input, cursor, sectionIndex);
    const placement = describeSectionPlacement(input, cursor, sectionIndex);
    const result = placeSection(input, cursor, placement);
    if (forcedNewPage || result.startedNewPage) pages.push([]);
    if (result.readableScale !== undefined) {
      sectionScales[sectionIndex] = result.readableScale;
    } else if (result.unreadable) {
      unreadableSectionIndexes.push(sectionIndex);
    }
    pages[pages.length - 1].push(sectionIndex);
  });
  return {
    pages,
    sectionScales,
    ...(unreadableSectionIndexes.length > 0
      ? { unreadableSectionIndexes }
      : {}),
  };
}

function compactPageBoundaries(
  input: ReportPaginationInput,
  layout: ReportPaginationLayout
): void {
  const maximumMoves =
    input.sectionHeights.length * Math.max(1, layout.pages.length - 1);
  for (let move = 0; move < maximumMoves; move += 1) {
    if (!compactOnePageBoundary(input, layout)) break;
  }
}

function compactOnePageBoundary(
  input: ReportPaginationInput,
  layout: ReportPaginationLayout
): boolean {
  const forcedBreaks = input.forcedPageBreakBeforeIndexes ?? new Set();
  for (let pageIndex = 0; pageIndex < layout.pages.length - 1; pageIndex += 1) {
    const nextSectionIndex = layout.pages[pageIndex + 1][0];
    if (nextSectionIndex !== undefined && !forcedBreaks.has(nextSectionIndex)) {
      const usedHeight = calculateUsedPageHeight(input, layout, pageIndex);
      const nextHeight = fittedSectionHeight(input, layout, nextSectionIndex);
      if (usedHeight + nextHeight <= getPageCapacity(input, pageIndex)) {
        layout.pages[pageIndex].push(nextSectionIndex);
        layout.pages[pageIndex + 1].shift();
        if (layout.pages[pageIndex + 1].length === 0)
          layout.pages.splice(pageIndex + 1, 1);
        return true;
      }
    }
  }
  return false;
}

function calculateUsedPageHeight(
  input: ReportPaginationInput,
  layout: ReportPaginationLayout,
  pageIndex: number
): number {
  return layout.pages[pageIndex].reduce(
    (total, sectionIndex) =>
      total + fittedSectionHeight(input, layout, sectionIndex),
    0
  );
}

function fittedSectionHeight(
  input: ReportPaginationInput,
  layout: ReportPaginationLayout,
  sectionIndex: number
): number {
  return (
    input.sectionHeights[sectionIndex] *
    (layout.sectionScales[sectionIndex] ?? 1)
  );
}

function getPageCapacity(
  input: ReportPaginationInput,
  pageIndex: number
): number {
  const availableHeight =
    pageIndex === 0
      ? input.availableHeight
      : input.continuationAvailableHeight ?? input.availableHeight;
  return calculatePageCapacity(
    availableHeight,
    input.renderedOverflowReserve ?? 0
  );
}

function calculateReadableSectionScale(
  sectionHeight: number,
  pageCapacity: number
): number | undefined {
  if (sectionHeight <= pageCapacity || pageCapacity <= 0) return undefined;
  const requiredScale = pageCapacity / sectionHeight;
  return requiredScale >= MIN_REPORT_SECTION_SCALE ? requiredScale : undefined;
}

export function calculateRenderedPageOverflow(page: PageHeightSource): number {
  return Math.max(0, page.scrollHeight - page.clientHeight);
}

function calculatePageCapacity(
  availableHeight: number,
  renderedOverflowReserve: number
): number {
  return Math.max(0, availableHeight - renderedOverflowReserve);
}
